import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibAddIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import moment from 'moment'
import { DateUtils } from '@pbt/pbt-ui-components'

import { useAgentChat } from './AgentChatContext'
import ChatHistoryDropdown from './ChatHistoryDropdown'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChatSessionBar.module.scss'

function sessionFallbackLabel(lastUpdateTime: number | undefined): string {
  if (lastUpdateTime == null) return ''
  return DateUtils.formatDate(
    moment.unix(lastUpdateTime).toISOString(),
    false,
    'MM/DD/YY h:mma',
  )
}

const ChatSessionBar: React.FC = () => {
  const { t } = useTranslation('Agent')
  const {
    session,
    sessions,
    selectSession,
    createNewSession,
    loadSessions,
    isLoadingSessions,
  } = useAgentChat()

  // Load sessions on mount
  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  const sortedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return []
    return [...sessions].sort((a, b) => {
      const aTime = a.lastUpdateTime || Date.now() / 1000
      const bTime = b.lastUpdateTime || Date.now() / 1000
      return bTime - aTime
    })
  }, [sessions])

  const sessionOptions = useMemo(() => {
    if (sortedSessions.length === 0) {
      return [
        {
          value: '',
          label: t('Agent:NO_CHAT_HISTORY'),
          lastUpdateTime: undefined,
        },
      ]
    }
    return sortedSessions.map((s) => ({
      value: s.id,
      label:
        s.state.title ||
        sessionFallbackLabel(s.lastUpdateTime) ||
        t('Agent:NO_CHAT_HISTORY'),
      lastUpdateTime: s.lastUpdateTime,
    }))
  }, [sortedSessions, t])

  const handleSessionSelect = (selectedId: string) => {
    const selectedSession = sessions.find((s) => s.id === selectedId)
    if (selectedSession) {
      selectSession(selectedSession)
    }
  }

  const handleNewChat = () => {
    createNewSession()
  }

  return (
    <div className={styles.sessionBar}>
      <div>
        <ChatHistoryDropdown
          ariaLabel={t('Agent:PAST_CHATS')}
          disabled={isLoadingSessions}
          options={sessionOptions}
          triggerClassName={classNames(
            styles.sessionBarButton,
            styles.historyDropdownTrigger,
          )}
          triggerLabel={t('Agent:PAST_CHATS')}
          value={session?.id ?? ''}
          onSelect={handleSessionSelect}
        />
      </div>
      <KibButtonNew
        aria-label={t('Agent:NEW_CHAT')}
        className={styles.newChatButton}
        emphasis="tertiary"
        icon={<KibAddIcon />}
        iconPosition="start"
        size="small"
        theme="utility"
        title={t('Agent:NEW_CHAT')}
        onClick={handleNewChat}
      >
        {t('Agent:NEW_CHAT')}
      </KibButtonNew>
    </div>
  )
}

export default ChatSessionBar
